import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gauge, ChevronRight, ShieldAlert, ListTree, ArrowRight } from "lucide-react";

const TONE_ACCENT = { addressable: "border-l-teal-500", downstream: "border-l-slate-400", review: "border-l-amber-500" };

const STATE_CLS = {
  IMPLEMENTED: "bg-emerald-50 text-emerald-700 border-emerald-300",
  SIMULATED: "bg-sky-50 text-sky-700 border-sky-300",
  "FUTURE INTEGRATION": "bg-slate-100 text-slate-600 border-slate-300",
};

export default function PilotMeasurement() {
  const [data, setData] = useState(null);
  const [tax, setTax] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.pilotMeasurement().then(setData).catch(() => {});
    api.denialTaxonomy().then(setTax).catch(() => {});
  }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading pilot measurement model…</div>;

  const s = data.shadow_summary || {};
  return (
    <div>
      <PageHeader testid="pilot-measurement-header" title="Pilot Measurement Model"
        subtitle="Connects each RequiSure recommendation to the eventual human, testing, claim, and financial outcome — so pilot value can be measured on real data, not asserted." />
      <InfoNote className="mb-4">{data.guardrail}</InfoNote>

      <Card className="mb-4 p-4" data-testid="pm-chain">
        <div className="mb-3 text-sm font-semibold text-slate-800">Recommendation → Outcome Chain</div>
        <div className="flex items-stretch gap-1 overflow-x-auto">
          {data.chain.map((n, i) => (
            <React.Fragment key={n.step}>
              <div className="flex min-w-[150px] flex-1 flex-col rounded-md border border-slate-200 p-2.5" data-testid={`pm-step-${i}`}>
                <span className="text-xs font-bold text-slate-900">{n.step}</span>
                <span className="text-[10px] text-slate-500">{n.source}</span>
                <span className={`mt-1.5 inline-flex w-fit rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${STATE_CLS[n.status] || "bg-slate-100 text-slate-600 border-slate-300"}`}>{n.status}</span>
              </div>
              {i < data.chain.length - 1 && <div className="flex items-center"><ChevronRight className="h-4 w-4 text-slate-300" /></div>}
            </React.Fragment>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4 lg:col-span-1" data-testid="pm-shadow-summary">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Gauge className="h-4 w-4 text-slate-400" /> Shadow-Mode Observations</div>
          <p className="mb-3 text-[11px] text-slate-500">{s.note}</p>
          <div className="space-y-2">
            <Stat label="Observations recorded" v={s.observations ?? 0} />
            <Stat label="Agreement" v={s.agreement ?? 0} />
            <Stat label="Disagreement" v={s.disagreement ?? 0} />
            <Stat label="Agreement rate" v={s.agreement_rate == null ? "Requires Real Pilot Data" : `${Math.round(s.agreement_rate * 100)}%`} />
          </div>
        </Card>

        <Card className="p-4 lg:col-span-2" data-testid="pm-metrics">
          <div className="mb-3 text-sm font-semibold text-slate-800">Pilot Metrics (defined · awaiting real data)</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {data.metrics.map((m) => (
              <div key={m.key} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`pm-metric-${m.key}`}>
                <span className="text-xs font-medium text-slate-700">{m.name}</span>
                <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">{m.note}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {tax && (
        <Card className="mt-4 p-4" data-testid="pm-taxonomy-summary">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><ListTree className="h-4 w-4 text-slate-400" /> Denial Taxonomy Context</div>
            <Button size="sm" variant="outline" data-testid="pm-taxonomy-link" onClick={() => nav("/denial-taxonomy")}>
              Open full taxonomy <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="mb-3 text-[11px] text-slate-500">How measured pilot outcomes will map to the first-pilot denial groups. The authoritative, detailed taxonomy lives on the dedicated Pilot Denial Taxonomy page.</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {tax.groups.map((g) => (
              <div key={g.id} className={`rounded-md border border-l-2 border-slate-200 bg-white p-2.5 ${TONE_ACCENT[g.tone] || "border-l-slate-300"}`} data-testid={`pm-tax-group-${g.id}`}>
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Group {g.id}</div>
                <div className="text-xs font-semibold text-slate-800">{g.name}</div>
                <div className="mt-1 text-[10px] leading-snug text-slate-500">{g.note}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="pm-tax-estimate">
            <span className="font-semibold text-slate-700">{tax.estimate.value_label}</span>
            <span className="flex-1 min-w-[200px]">{tax.estimate.statement}</span>
            <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-800">Collaborator Estimate — Requires Validation</span>
          </div>
        </Card>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs text-amber-900" data-testid="pm-guardrail">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        No predictive denial model or autonomous rule learning at this stage. All values are synthetic and for validation planning only.
      </div>
    </div>
  );
}

const Stat = ({ label, v }) => (
  <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="font-display text-sm font-bold text-slate-900">{v}</span>
  </div>
);
